/**
 * Turns whatever an error carried into one short, plain sentence for a toast. Server validation
 * messages ("Service name is required.") pass through; network failures, bare status codes,
 * server internals and code errors ("Unexpected token <", "Cannot read property…") become a
 * simple message or the caller's own fallback, which already says what failed.
 */

let language: 'en' | 'bn' = 'en';
/** Kept in step with the app language by useTranslation, so these messages follow it. */
export function setErrorLanguage(next: 'en' | 'bn'): void {
  language = next;
}
const say = (en: string, bn: string) => (language === 'bn' ? bn : en);

const NETWORK = /network request failed|failed to fetch|networkerror|network error|load failed|timed? ?out|timeout|aborted|econnrefused|enotfound|no internet/i;
const TECHNICAL =
  /internal server error|unexpected (token|end)|json|prisma|undefined|null is not|cannot read propert|is not a function|is not defined|stack|exception|<html|<!doctype|status code|bad gateway|service unavailable|gateway time|typeerror|syntaxerror|referenceerror/i;

function byStatus(status: number): string | null {
  if (status === 401) return say('Your session has ended. Please sign in again.', 'আপনার সেশন শেষ হয়েছে। আবার সাইন ইন করুন।');
  if (status === 403) return say("You don't have permission to do that.", 'এটি করার পারমিশন আপনার নেই।');
  if (status === 404) return say("We couldn't find that. It may have been removed.", 'এটি পাওয়া যায়নি। হয়তো ডিলিট করা হয়েছে।');
  if (status === 409) return say('That already exists.', 'এটি ইতিমধ্যে আছে।');
  if (status === 413) return say('That is too large to send.', 'এটি পাঠানোর জন্য খুব বড়।');
  if (status === 429) return say('Too many tries. Please wait a moment and try again.', 'অনেকবার চেষ্টা হয়েছে। একটু পরে আবার চেষ্টা করুন।');
  return null;
}

/** "durationMinutes must be…" (a field name from a default server message) → "Duration minutes must be…". */
function humanizeLeadingField(message: string): string {
  return message.replace(/^([a-z]+[A-Z][A-Za-z]*)\b/, (field) => field.replace(/([A-Z])/g, ' $1').toLowerCase());
}

/**
 * Messages users meet most often (sign-in, passwords, codes, joining a shop, the app's own thrown
 * errors), in Bengali. The server and the services send English; anything not listed here still
 * shows as sent. Matched on the whole message, case-insensitive, ignoring a trailing full stop.
 */
const BN_MESSAGES: [RegExp, string][] = [
  [/^(email or password is incorrect|invalid credentials)$/, 'ইমেইল বা পাসওয়ার্ড সঠিক নয়।'],
  [/^incorrect (current )?password$/, 'পাসওয়ার্ড সঠিক নয়।'],
  [/^new password must be at least 8 characters long$/, 'নতুন পাসওয়ার্ড কমপক্ষে ৮ অক্ষরের হতে হবে।'],
  [/^new password must be different from current password$/, 'নতুন পাসওয়ার্ড কারেন্ট পাসওয়ার্ড থেকে আলাদা হতে হবে।'],
  [/^(email already in use|another account already uses this email)$/, 'এই ইমেইল ইতিমধ্যে ব্যবহৃত হচ্ছে।'],
  [/^(account|user) not found$/, 'অ্যাকাউন্ট পাওয়া যায়নি।'],
  [/^this account (is|has been) (deleted|already deleted)$/, 'এই অ্যাকাউন্ট ডিলিট করা হয়েছে।'],
  [/^this account has been suspended$/, 'এই অ্যাকাউন্ট স্থগিত করা হয়েছে।'],
  [/^invalid or expired code$/, 'কোডটি ভুল অথবা মেয়াদ শেষ।'],
  [/^invalid or expired refresh token$/, 'আপনার সেশন শেষ হয়েছে। আবার সাইন ইন করুন।'],
  [/^session expired\. please log in again$/, 'আপনার সেশন শেষ হয়েছে। আবার সাইন ইন করুন।'],
  [/^not authenticated$/, 'আপনি সাইন ইন করেননি।'],
  [/^too many wrong codes\..*/, 'অনেকবার ভুল কোড দেওয়া হয়েছে। নতুন কোড চেয়ে আবার চেষ্টা করুন।'],
  [/^this code has expired\..*/, 'এই কোডের মেয়াদ শেষ। নতুন কোড চান।'],
  [/^this code was already used$/, 'এই কোডটি আগেই ব্যবহার করা হয়েছে।'],
  [/^this request was already handled$/, 'এই রিকোয়েস্টটি আগেই কমপ্লিট হয়েছে।'],
  [/^this request has expired\..*/, 'এই রিকোয়েস্টের মেয়াদ শেষ। নতুন রিকোয়েস্ট পাঠাতে বলুন।'],
  [/^this request is gone\..*/, 'এই রিকোয়েস্টটি আর নেই।'],
  [/^this account is already linked to a shop$/, 'এই অ্যাকাউন্ট ইতিমধ্যে একটি শপের সাথে যুক্ত।'],
  [/^only stylist accounts can join a shop$/, 'শুধু স্টাইলিস্ট অ্যাকাউন্ট শপে জয়েন করতে পারে।'],
  [/^no shop is associated with this account yet$/, 'এই অ্যাকাউন্টে এখনো কোনো শপ যুক্ত নেই।'],
  [/^(shop|service) not found$/, 'এটি পাওয়া যায়নি।'],
  [/^name cannot be empty$/, 'নাম খালি রাখা যাবে না।'],
  [/^google sign-in failed\. please try again$/, 'গুগল সাইন-ইন ব্যর্থ হয়েছে। আবার চেষ্টা করুন।'],
  [/^signing in with google needs google play services on this phone$/, 'গুগল দিয়ে সাইন ইন করতে এই ফোনে গুগল প্লে সার্ভিসেস লাগবে।'],
  [/^that google account is not the one this account uses$/, 'এটি আপনার অ্যাকাউন্টে ব্যবহৃত গুগল অ্যাকাউন্ট নয়।'],
  [/^google sign-in isn.t (available|set up) .*/, 'এই সংস্করণে গুগল সাইন-ইন চালু নেই।'],
  [/^this account is a customer account\..*/, 'এটি কাস্টমার অ্যাকাউন্ট। SalunBD কাস্টমার অ্যাপ থেকে সাইন ইন করুন।'],
  [/^admin accounts must sign in from the salunbd admin console$/, 'অ্যাডমিন অ্যাকাউন্ট অ্যাডমিন কনসোল থেকে সাইন ইন করতে হবে।'],
  [/^email and password are required$/, 'ইমেইল ও পাসওয়ার্ড দিন।'],
  [/^name, email, and password are required$/, 'নাম, ইমেইল ও পাসওয়ার্ড দিন।'],
  [/^please enter your shop name$/, 'আপনার শপের নাম লিখুন।'],
  [/^upload succeeded but the server response could not be read\..*/, 'আপলোড হয়েছে, কিন্তু উত্তর পড়া যায়নি। রিফ্রেশ করুন।'],
  [/^could not resolve an address for this location$/, 'এই লোকেশনের অ্যাড্রেস পাওয়া যায়নি।'],
  [/^could not search for that address$/, 'অ্যাড্রেসটি খোঁজা যায়নি।'],
  [/^could not load your new shop\..*/, 'আপনার নতুন শপ লোড করা যায়নি। আবার সাইন ইন করুন।'],
];

function translateKnown(text: string): string | null {
  const key = text.replace(/[.\s]+$/, '').trim().toLowerCase();
  for (const [re, bn] of BN_MESSAGES) if (re.test(key)) return bn;
  return null;
}

export function simplifyErrorMessage(raw: unknown, fallback: string, status?: number): string {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (text && NETWORK.test(text)) {
    return say('No internet connection. Check your connection and try again.', 'ইন্টারনেট কানেকশন নেই। কানেকশন দেখে আবার চেষ্টা করুন।');
  }
  const statusInText = /request failed \((\d{3})\)/i.exec(text)?.[1];
  const code = status ?? (statusInText ? Number(statusInText) : undefined);
  if (!text || statusInText || TECHNICAL.test(text)) {
    if (code && code >= 500) return fallback;
    return (code && byStatus(code)) || fallback;
  }
  if (language === 'bn') {
    const known = translateKnown(text);
    if (known) return known;
  }
  if (/should not exist/i.test(text)) {
    return say("Some of the details aren't accepted. Please check the form and try again.", 'কিছু তথ্য গ্রহণযোগ্য নয়। ফর্মটি দেখে আবার চেষ্টা করুন।');
  }
  // One sentence is enough for a toast.
  let message = text.split(/(?<=[.!?।])\s+/)[0];
  if (message.length > 160) message = `${message.slice(0, 157).trimEnd()}…`;
  message = humanizeLeadingField(message);
  return message.charAt(0).toUpperCase() + message.slice(1);
}
